/**
 * CollectionTargetBar — this month's credit collection target vs. what has
 * actually come in, as a progress bar with a "where the month is" pace marker.
 *
 * Rendered in the Credit sheet's summary band (Credit.jsx).
 *
 * WHY IT REFETCHES INSTEAD OF SUMMING THE ROWS IN VIEW
 * The neighbouring stat cards derive from `filteredDataArray`, so they follow
 * the sheet's filters. This one deliberately does not, because the target has a
 * definition that the sheet's rows cannot reproduce:
 *
 *   - The Credit sheet only carries StatusFlag='Credit'. The collection query
 *     spans 'Credit' AND 'CreditCleared' — a deal cleared on the 10th still owed
 *     an installment this month and still contributed the money that paid it.
 *     Dropping it would understate both halves of the ratio.
 *   - The sheet has no per-row "paid this month"; its TotalPaid is life-to-date.
 *
 * So it reads /getcreditcollectiontrend, the same endpoint behind the
 * dashboard's Credit Collection Overview card, and reports the same figures a
 * manager sees there. Two screens disagreeing about the month's collection
 * would be worse than a bar that ignores a filter.
 */
import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GlobalContext } from './GlobalContext';
import { apiFetch } from './api.js';
import { formatUGX } from './UtilityFunctions';
import { DEFAULT_COLLECTION_MONTHS, currentMonthRow } from './creditCollection';

//Semantic status colors are never tenant-themed (see MODAL_STYLE.md, and the
//same note in components/DebtorRiskPanel.jsx) — a bar that says "behind" has to
//read as behind under every theme preset.
const AHEAD = '#059669';   // emerald 600
const BEHIND = '#d97706';  // amber 600

export default function CollectionTargetBar() {
    const { DealershipID } = useContext(GlobalContext);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['creditCollectionTrend', DealershipID, DEFAULT_COLLECTION_MONTHS],
        queryFn: async () => {
            const response = await apiFetch('/getcreditcollectiontrend', {
                body: { DealershipID, months: DEFAULT_COLLECTION_MONTHS },
            });
            if (!response.ok) throw new Error(`Server responded with status: ${response.status}`);
            const result = await response.json();
            return result.data;
        },
        enabled: Boolean(DealershipID),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
    });

    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const dayOfMonth = now.getDate();
    const pacePct = (dayOfMonth / daysInMonth) * 100;

    // Matched by 'YYYY-MM' key, not by position in the series — see
    // creditCollection.js for why position drifts.
    const row = currentMonthRow(data, now);

    // Due + overdue, the same total the dashboard's "Total" category shows: an
    // installment missed in June is still money the team is chasing in August,
    // and the team is measured on collecting it.
    const target = (Number(row?.DueTarget) || 0) + (Number(row?.OverdueTarget) || 0);
    const collected = (Number(row?.DueCollected) || 0) + (Number(row?.OverdueCollected) || 0);

    // A dealership that has never filled in MonthlyPayment has no target, and
    // a percentage of zero is not a number worth rendering.
    const hasTarget = target > 0;
    const pct = hasTarget ? (collected / target) * 100 : 0;
    const fillPct = Math.min(pct, 100);
    const onTrack = pct >= Math.min(pacePct, 100);
    const color = onTrack ? AHEAD : BEHIND;

    if (isLoading) {
        return (
            <div className="min-w-0 max-w-[360px] flex-1" id="credit-collection-target">
                <div className="h-3 w-32 mb-2 rounded bg-gray-100 animate-pulse" />
                <div className="h-3 w-full rounded bg-gray-100 animate-pulse" />
            </div>
        );
    }

    //A failed side panel must not take the sheet's summary band with it.
    if (isError || !row) return null;

    const tooltip = hasTarget
        ? `Target: the monthly installment agreed on every credit deal that was still owing this month, `
          + `including deals already past their due date and deals cleared during the month.\n`
          + `Collected: credit repayments (AR) dated this month.\n`
          + `Same figures as the dashboard's Credit Collection Overview.`
        : 'No target for this month — no open credit deal has a Monthly Payment set.';

    return (
        <div
            className="min-w-0 max-w-[360px] flex-1"
            id="credit-collection-target"
            title={tooltip}
        >
            <div className="flex items-baseline justify-between gap-2 leading-none">
                <span className="text-[11px] font-medium text-gray-500">Collected this month</span>
                {hasTarget && (
                    <span className="text-[11px] font-bold tabular-nums" style={{ color }}>
                        {Math.round(pct)}%
                    </span>
                )}
            </div>

            <div
                className="relative mt-1 h-3 w-full overflow-hidden rounded bg-gray-100"
                role="progressbar"
                aria-label="Credit collected this month against target"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pct)}
            >
                <div
                    className="h-full rounded transition-all duration-500"
                    style={{ width: `${fillPct}%`, background: color }}
                />
                {/* Pace marker: how far through the month we are. Without it a
                    bare "58%" is unreadable — good on the 22nd, alarming on the
                    2nd. Sits above the fill so it stays visible once passed.
                    Held just short of the end because on the last day of the
                    month pacePct is exactly 100, and a 1px rule at left:100%
                    falls outside the track and is clipped away by
                    overflow-hidden — losing the marker on the one day it matters
                    most. */}
                {hasTarget && (
                    <div
                        className="absolute top-0 h-full w-px bg-gray-600/70"
                        style={{ left: `${Math.min(pacePct, 99.5)}%` }}
                    />
                )}
            </div>

            <div className="mt-1 flex items-baseline justify-between gap-2 leading-none">
                <span className="truncate text-[11px] tabular-nums text-gray-600">
                    {hasTarget
                        ? `${formatUGX(collected, { abbreviate: true })} of ${formatUGX(target, { abbreviate: true, symbol: false })}`
                        : `${formatUGX(collected)} collected · no target set`}
                </span>
                <span className="text-[11px] whitespace-nowrap text-gray-400">
                    Day {dayOfMonth}/{daysInMonth}
                </span>
            </div>
        </div>
    );
}
